<template>
  <div v-if="regroupementProduit">
    <div class="entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3"> {{ getTitle }} </h1>
              <app-breadcrumb :breadcrumb="breadcrumb"/>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="buttons">
            <button class="button" @click="$router.go(-1)">
              {{ $t('general.button.cancel')}}
            </button>
            <button class="button is-addOrEdit" @click="save()">
              {{ $t('general.button.sauvegarder') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <qd-message
      v-if="!regroupementProduit.produits.length"
      :title="$t('addEditRegroupementProduit.bMessage.info')"
      type="is-info" class="mgt-20px" has-icon>
      {{ $t('addEditRegroupementProduit.bMessage.infoMsg') }}
    </qd-message>
    <error-msg-component
      v-if="error"
      :messages="messages"
      :error="error"
      :champs-vide="champsVide"
      :messages-champs-vide="messagesChampsVide"
      @close="error = $event"
      class="mgt-20px"
      title="general.problemOccurred"
      withEmplyFields/>
    <div class="tab">
      <ul>
        <li
          v-for="(tab, index) in tabs" :key="index"
          :class="{'is-active': activeTab === tab.component, 'is-disabled': tab.disabled}"
          @click="activeTab = tab.component">
          {{ $t(`addEditRegroupementProduit.tabs.${tab.component}`) }}
        </li>
      </ul>
    </div>
    <section class="corps">
      <component
        :is="activeTab"
        :detail="false"
        :regroupementProduit="regroupementProduit"
        :produit="regroupementProduit"
        :permissions="constantes.CATALOGUE"
        :breadcrumb="breadcrumb"
      />
    </section>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { get, create, update } from '@/api/produit/regroupementProduit';
import messageErreur from '@/services/general/messageErreur';
import messageSucces from '@/services/general/messageSucces';
import formatIsoDate from '@/services/general/formatIsoDate';
import validationGeneral, { removeDuplicateMsgError } from '@/services/validation/validation';
import genererRegroupementProduit from '@/services/presse/regroupementProduit/genererRegroupementProduit';
import translate from '@/services/presse/regroupementProduit/translatorRegroupementProduit';
import validationRegroupementProduit from '@/services/presse/regroupementProduit/validationRegroupementProduit';
import validationProduit from '@/services/catalogue/validation/validationProduit';
import AppBreadcrumb from '@/components/general/breadcrumb';
import errorMsgComponent from '@/components/general/errorMsgComponent';
import Informations from '@/components/presse/regroupementProduit/informations';
import Produits from '@/components/presse/regroupementProduit/produits';
import regleCommission from '@/components/catalogue/produitGeneralPresse/edition/regleCommission';
import objectifVente from '@/components/catalogue/produitGeneralPresse/edition/objectifVente';
import reglesAchat from '@/components/catalogue/produitGeneralPresse/edition/reglesAchat';
import cgv from '@/components/catalogue/produitGeneralPresse/edition/cgv';
import { CATALOGUE } from '@/configuration/permissionsBodacConf';
import { SET_REGROUPEMENT_PRODUIT } from '@/store/modules/bodac/catalogue/regroupementProduit-mutation-type';

const Societe = createNamespacedHelpers('societe');
const RegroupementProduit = createNamespacedHelpers('regroupementProduit');

export default {
  components: {
    AppBreadcrumb,
    errorMsgComponent,
    Informations,
    Produits,
    regleCommission,
    objectifVente,
    reglesAchat,
    cgv,
  },
  data() {
    return {
      regroupementProduit: null,
      error: false,
      messages: [],
      champsVide: [],
      messagesChampsVide: [],
      libelle: '',
      activeTab: 'informations',
      constantes: {
        CATALOGUE,
      },
    };
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    ...RegroupementProduit.mapGetters({
      regroupementProduitSaveToStore: 'regroupementProduit',
    }),
    tabs() {
      return [
        {
          component: 'informations',
          disabled: false,
        },
        {
          component: 'produits',
          disabled: false,
        },
        {
          component: 'regleCommission',
          disabled: !this.regroupementProduit.produits.length,
        },
        {
          component: 'objectifVente',
          disabled: !this.regroupementProduit.produits.length,
        },
        {
          component: 'reglesAchat',
          disabled: !this.regroupementProduit.produits.length,
        },
        {
          component: 'cgv',
          disabled: !this.regroupementProduit.produits.length,
        },
      ];
    },
    getTitle() {
      let title = this.dataAlreadyExist
        ? this.$t('addEditRegroupementProduit.title.edit')
        : this.$t('addEditRegroupementProduit.title.add');
      if (this.dataAlreadyExist) title += ' ' + this.libelle;
      return title;
    },
    dataAlreadyExist() {
      return this.regroupementProduit && !!(this.regroupementProduit.id);
    },
    breadcrumb() {
      return [
        { title: this.$i18n.t('addEditRegroupementProduit.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t('addEditRegroupementProduit.breadcrumb.liste'), route: { name: 'l.bodac.catalogue-regroupementProduit' }, isActive: false },
        { title: this.dataAlreadyExist ? this.$i18n.t('addEditRegroupementProduit.breadcrumb.edit') : this.$t('addEditRegroupementProduit.breadcrumb.add'), route: { name: '' }, isActive: true },
      ];
    },
  },
  methods: {
    ...RegroupementProduit.mapMutations({
      setRegroupementProduit: SET_REGROUPEMENT_PRODUIT,
    }),
    async save() {
      this.error = false;
      this.messages = [];
      this.champsVide = [];
      this.messagesChampsVide = [];
      const loadingComponent = this.$buefy.loading.open();
      try {
        let regroupementProduit = cloneDeep(this.regroupementProduit);
        regroupementProduit.societeId = this.societeActiveId;
        regroupementProduit.commissionsVendeur.forEach((el) => {
          el.dateDebut = formatIsoDate(el.dateDebut);
          if (el.dateFin) el.dateFin = formatIsoDate(el.dateFin);
        });
        regroupementProduit.reglesAchat.forEach(item => {
          item.dateDebut = formatIsoDate(item.dateDebut);
          if (item.dateFin) item.dateFin = formatIsoDate(item.dateFin);
        });
        regroupementProduit.produitsId = regroupementProduit.produits.map(p => p.id);
        if (!this.dataAlreadyExist) {
          regroupementProduit = (await create(regroupementProduit)).data.result;
          messageSucces(this.$i18n.t('addEditRegroupementProduit.notifications.successCreation'));
        } else {
          regroupementProduit = (await update(regroupementProduit)).data.result;
          messageSucces(this.$i18n.t('addEditRegroupementProduit.notifications.successChange'));
        }
        await this.$router.push({ name: 'c.bodac.catalogue-detailRegroupementProduit', params: { id: regroupementProduit.id } });
        loadingComponent.close();
      } catch (e) {
        loadingComponent.close();
        if (e.response && e.response.data.error) {
          validationGeneral(this.$i18n, e.response, this.champsVide,
            this.messages, this.messagesChampsVide, translate);
          validationRegroupementProduit(e.response, this.messages);
          this.messages.push(...validationProduit(e.response));
          this.messages = removeDuplicateMsgError(this.messages);
          this.error = true;
          return;
        }
        throw e;
      }
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      let regroupementProduit = genererRegroupementProduit();
      if (this.regroupementProduitSaveToStore) {
        regroupementProduit = this.regroupementProduitSaveToStore;
        this.libelle = regroupementProduit.libelle;
        this.setInformationPage(this.$t('pageGeneral.modification', { item: this.libelle }));
        this.setRegroupementProduit(null);
      } else if (this.$route.params.id) {
        regroupementProduit = (await get(this.$route.params.id)).data.result;
        this.libelle = regroupementProduit.libelle;
        this.setInformationPage(this.$t('pageGeneral.modification', { item: this.libelle }));
      } else {
        this.setInformationPage(this.$t('pageGescom.regroupementProduit.creation'));
      }
      regroupementProduit.commissionsVendeur.forEach((el) => {
        el.dateDebut = new Date(el.dateDebut);
        if (el.dateFin) el.dateFin = new Date(el.dateFin);
        el.selected = false;
      });
      regroupementProduit.reglesAchat.forEach((el) => {
        el.dateDebut = new Date(el.dateDebut);
        if (el.dateFin) el.dateFin = new Date(el.dateFin);
        el.selected = false;
      });
      regroupementProduit.objectifsVente.forEach((el) => { el.selected = false; });
      regroupementProduit.cgvs.forEach((el) => { el.selected = false; });
      this.regroupementProduit = regroupementProduit;
      loadingComponent.close();
    } catch (e) {
      loadingComponent.close();
      messageErreur(this.$i18n);
      throw e;
    }
  },
  beforeRouteEnter(to, from, next) {
    let tab = '';
    switch (from.name) {
      case 'm.bodac.catalogue-ajoutProduitsSurRegroupement':
        tab = 'produits';
        break;
      default:
        tab = 'informations';
    }
    next(vm => {
      vm.activeTab = tab;
    });
  },
};
</script>
